import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { Section, Overline } from "@/components/site/Section";
import CtaBanner from "@/components/site/CtaBanner";

const INDUSTRIES = [
  {
    key: "technology",
    name: "Technology & SaaS",
    blurb: "Scale rapidly with highly skilled technical professionals across software, cloud, security, AI, and product.",
    intro: "We understand the competitive nature of technology hiring and help organizations secure top talent faster while reducing hiring bottlenecks.",
    roles: [
      "Software Engineers", "Full Stack Developers", "Frontend Developers", "Backend Developers",
      "DevOps Engineers", "Cloud Engineers", "AI & Machine Learning Engineers",
      "Cybersecurity Analysts", "Data Engineers", "Product Managers",
      "QA Engineers", "Solutions Architects",
    ],
  },
  {
    key: "healthcare",
    name: "Healthcare",
    blurb: "Clinical, operational, and administrative recruiting focused on compliance, quality, and continuity.",
    intro: "We support healthcare organizations with strategic recruiting solutions for clinical, operational, and administrative roles.",
    roles: [
      "Registered Nurses", "Nurse Practitioners", "Healthcare Administrators",
      "Medical Assistants", "Clinical Coordinators", "Healthcare Recruiters",
      "Medical Billing Specialists", "Physical Therapists",
      "Occupational Therapists", "Behavioral Health Professionals",
    ],
  },
  {
    key: "aerospace",
    name: "Aerospace & Aviation",
    blurb: "Specialized engineering and technical talent for mission-critical aerospace and aviation programs.",
    intro: "We help aerospace and aviation companies secure specialized engineering and technical talent required for mission-critical projects.",
    roles: [
      "Aerospace Engineers", "Avionics Engineers", "Systems Engineers",
      "Mechanical Engineers", "Electrical Engineers", "Manufacturing Engineers",
      "Program Managers", "Quality Engineers", "Supply Chain Specialists",
      "FAA Compliance Professionals",
    ],
  },
  {
    key: "engineering",
    name: "Engineering & Manufacturing",
    blurb: "Workforce solutions that improve productivity, project execution, and operational efficiency.",
    intro: "We support engineering and manufacturing companies with workforce solutions designed to scale.",
    roles: [
      "Electrical Engineers", "Mechanical Engineers", "Controls Engineers",
      "Industrial Engineers", "Process Engineers", "Automation Engineers",
      "Manufacturing Managers", "Maintenance Technicians",
      "Production Supervisors", "Plant Managers",
    ],
  },
  {
    key: "finance",
    name: "Finance & Accounting",
    blurb: "Skilled finance and accounting professionals supporting growth, compliance, and performance.",
    intro: "We help organizations identify skilled finance and accounting professionals who support operational growth.",
    roles: [
      "Accountants", "Senior Accountants", "Financial Analysts",
      "Controllers", "Tax Specialists", "Payroll Specialists",
      "Auditors", "Accounts Payable Specialists",
      "Accounts Receivable Specialists", "CFOs",
    ],
  },
  {
    key: "legal",
    name: "Legal",
    blurb: "Confidential, specialized recruiting for law firms and corporate legal departments.",
    intro: "We provide recruiting solutions for law firms, corporate legal departments, and compliance-driven organizations.",
    roles: [
      "Attorneys", "Litigation Attorneys", "Corporate Attorneys",
      "Real Estate Attorneys", "Legal Assistants", "Paralegals",
      "Compliance Specialists", "Contract Administrators",
      "Legal Secretaries", "Document Review Specialists",
    ],
  },
  {
    key: "hr",
    name: "Human Resources",
    blurb: "Talent acquisition, HR operations, employee relations, and HR leadership recruiting.",
    intro: "We support businesses and HR consulting firms with recruiting solutions for the HR function itself.",
    roles: [
      "HR Managers", "Talent Acquisition Specialists", "Recruiters",
      "HR Business Partners", "Compensation Analysts", "Employee Relations Specialists",
      "HR Coordinators", "Learning & Development Specialists", "HR Directors",
    ],
  },
];

function IndustriesHero() {
  return (
    <Section className="pb-12">
      <Overline className="mb-5">Industries we serve</Overline>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[1.05] max-w-4xl">
        Specialized recruiting across <span className="italic text-[hsl(var(--primary))]">seven verticals.</span>
      </h1>
      <p className="mt-6 max-w-3xl text-lg md:text-xl text-muted-foreground">
        Each industry has its own hiring rhythm, candidate expectations, and compliance needs.
        We bring dedicated expertise to every engagement.
      </p>
    </Section>
  );
}

function IndustryTabs({ active, onSelect }) {
  return (
    <div className="lg:col-span-4 bg-[hsl(var(--background))]">
      {INDUSTRIES.map((ind, idx) => {
        const isActive = ind.key === active;
        return (
          <button
            key={ind.key}
            data-testid={`industry-tab-${ind.key}`}
            onClick={() => onSelect(ind.key)}
            className={`w-full text-left p-6 md:p-7 border-b border-border last:border-b-0 flex items-start justify-between gap-3 group transition-colors ${
              isActive ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-[hsl(var(--secondary))]/50"
            }`}
          >
            <div>
              <p className={`text-xs tracking-widest uppercase ${isActive ? "text-white/70" : "text-muted-foreground"}`}>
                {String(idx + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-1 font-display text-xl md:text-2xl">{ind.name}</h3>
            </div>
            <ChevronRight className={`h-5 w-5 mt-2 transition-transform ${isActive ? "translate-x-1" : "opacity-50 group-hover:opacity-100"}`} />
          </button>
        );
      })}
    </div>
  );
}

function IndustryDetail({ industry }) {
  return (
    <div className="lg:col-span-8 bg-[hsl(var(--background))] p-8 md:p-12">
      <Overline className="mb-3">How we serve</Overline>
      <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight" data-testid="industry-detail-name">
        {industry.name}
      </h2>
      <p className="mt-5 text-muted-foreground text-lg leading-relaxed">{industry.intro}</p>
      <p className="mt-3 text-muted-foreground leading-relaxed">{industry.blurb}</p>

      <h3 className="mt-10 font-display text-xl mb-4">Major roles we support</h3>
      <div className="flex flex-wrap gap-2">
        {industry.roles.map((r) => (
          <span key={r} className="px-3 py-1.5 rounded-md bg-[hsl(var(--secondary))] text-foreground/80 text-xs border border-border">
            {r}
          </span>
        ))}
      </div>

      <div className="mt-10">
        <Link
          to="/contact"
          data-testid="industry-detail-cta"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[hsl(var(--accent))] text-white text-sm font-medium hover:bg-[#c84d2e] transition-colors"
        >
          Discuss {industry.name} hiring <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

export default function Industries() {
  const [active, setActive] = useState(INDUSTRIES[0].key);
  const current = INDUSTRIES.find((i) => i.key === active);

  return (
    <div data-testid="page-industries">
      <IndustriesHero />
      <Section className="pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border border border-border rounded-xl overflow-hidden">
          <IndustryTabs active={active} onSelect={setActive} />
          <IndustryDetail industry={current} />
        </div>
      </Section>
      <CtaBanner
        title="Don't see your industry? We likely still serve it."
        ctaLabel="Talk to a recruiter"
        testid="industries-cta"
        topPadding={false}
      />
    </div>
  );
}
